import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Customized
} from 'recharts';
import Form from 'react-bootstrap/Form';
import { useLocation } from 'react-router-dom';
import ChartContext from './DataEntrySection/DataEntryProvider';
import { wFlagIChartColumns, oocFlagIChartColumns } from '../components/Columns';
import { createCustomizedDot, getSigDigitsUtil } from '../util/util';
import { getConfiguration } from '../util/config';

function SQCLineChart({
  data,
  lines = [],
  syncId,
  xAxis,
  yMin,
  yMax,
  isEverFlags,
  chartSigDigits,
  controlStrategyConfigurations,
  selectedSummaryInfo,
  unit = null
}) {
  const location = useLocation();
  const isSpcDashboard = location.pathname === '/spc-dashboard';
  const formatXAxis = (tickItem) => {
    return String(data[tickItem]?.date) || '';
  };
  const { currentControlStrategyConfigurations } =
    controlStrategyConfigurations || useContext(ChartContext);
  const [setupDataChecked, setSetupDataChecked] = useState(false);
  const [warningFlagsChecked, setWarningFlagsChecked] = useState(true);
  const [outOfControlChecked, setOutOfControlChecked] = useState(true);
  const [runChartsChecked, setRunChartsChecked] = useState(true);
  const [everFlagsChecked, setEverFlagsChecked] = useState(false);
  const config = getConfiguration(window.location.hostname);

  const renderSigDigits = (stringValue) => {
    return getSigDigitsUtil(
      stringValue,
      currentControlStrategyConfigurations?.chartSigDigits || chartSigDigits || 3
    );
  };

  const formatYAxis = (tickItem) => {
    return renderSigDigits(tickItem);
  };

  const handleSetupDataCheck = (e) => {
    setSetupDataChecked(e.target.checked);
  };

  const handleWarningFlagCheck = (e) => {
    setWarningFlagsChecked(e.target.checked);
  };

  const handleOutOfControlCheck = (e) => {
    setOutOfControlChecked(e.target.checked);
  };

  const handleRunChartsCheck = (e) => {
    setRunChartsChecked(e.target.checked);
  };

  const handleEverFlagsCheck = (e) => {
    setEverFlagsChecked(e.target.checked);
  };

  const renderToleranceLines = (line) => {
    if (line.name === 'User Defined LTL') {
      return (
        <Line
          type="linear"
          key={uuidv4()}
          isAnimationActive={false}
          dataKey={line.yAxis}
          stroke={line.color}
          label="Test"
          name={line.name}
          dot={<CustomizedDot key={uuidv4()} />}
          strokeDasharray="12"
        />
      );
    }
    if (line.name === 'User Defined UTL') {
      return (
        <Line
          type="linear"
          key={uuidv4()}
          isAnimationActive={false}
          dataKey={line.yAxis}
          stroke={line.color}
          label="Test"
          name={line.name}
          dot={<CustomizedDot key={uuidv4()} />}
          strokeDasharray="12"
        />
      );
    }
    return <></>;
  };

  const renderControlLines = (line) => {
    if (line.name === 'User Defined LCL') {
      return (
        <Line
          type="linear"
          key={uuidv4()}
          isAnimationActive={false}
          dataKey={line.yAxis}
          stroke={line.color}
          label="Test"
          name={line.name}
          dot={<CustomizedDot key={uuidv4()} />}
          strokeDasharray="12"
        />
      );
    }
    if (line.name === 'User Defined UCL') {
      return (
        <Line
          type="linear"
          key={uuidv4()}
          isAnimationActive={false}
          dataKey={line.yAxis}
          stroke={line.color}
          label="Test"
          name={line.name}
          dot={<CustomizedDot key={uuidv4()} />}
          strokeDasharray="12"
        />
      );
    }
    return <></>;
  };

  const renderReferenceLines = (rcc) => {
    if (!rcc) return null;
    if (rcc) {
      return (
        <>
          {lines
            .filter(
              (l) =>
                l.reference &&
                ![
                  'User Defined LTL',
                  'User Defined UTL',
                  'User Defined LCL',
                  'User Defined UCL'
                ].includes(l.name)
            )
            .map((line) => (
              <Line
                type="linear"
                key={uuidv4()}
                isAnimationActive={false}
                dataKey={line.yAxis}
                stroke={line.color}
                label="Test"
                name={line.name}
                dot={<CustomizedDot key={uuidv4()} />}
              />
            ))}
          {lines
            .filter(
              (l) =>
                l.reference &&
                ![
                  'User Defined LTL',
                  'User Defined UTL',
                  'User Defined LCL',
                  'User Defined UCL'
                ].includes(l.name)
            )
            .map((line) => (
              <ReferenceLine
                y={line.overrideValue || data[data.length - 1][line.yAxis]}
                key={uuidv4()}
                stroke="transparent"
                label={line.label}
              />
            ))}
          {lines
            .filter(
              (l) => l.reference && (l.name === 'User Defined LTL' || l.name === 'User Defined UTL')
            )
            .map((line) => renderToleranceLines(line))}
          {lines
            .filter(
              (l) => l.reference && l.name !== 'User Defined LCL' && l.name !== 'User Defined UCL'
            )
            .map((line) => (
              <ReferenceLine
                y={line.overrideValue || data[data.length - 1][line.yAxis]}
                key={uuidv4()}
                stroke="transparent"
                label={line.label}
              />
            ))}
          {lines
            .filter(
              (l) => l.reference && (l.name === 'User Defined LCL' || l.name === 'User Defined UCL')
            )
            .map((line) => renderControlLines(line))}
        </>
      );
    }

    return (
      <>
        {lines
          .filter((l) => l.reference)
          .map((line) => (
            <Line
              type="linear"
              key={uuidv4()}
              isAnimationActive={false}
              dataKey={line.yAxis}
              stroke="transparent"
              label=""
              name={line.name}
              dot={<CustomizedDot key={uuidv4()} />}
            />
          ))}
        {lines
          .filter((l) => l.reference)
          .map((line) => (
            <ReferenceLine
              y={line.overrideValue || data[data.length - 1][line.yAxis]}
              key={uuidv4()}
              stroke="transparent"
              label=""
            />
          ))}
      </>
    );
  };

  const checkWarning = (payload) => {
    return wFlagIChartColumns.find((f) => !!payload[f]) !== undefined;
  };

  const checkOOC = (payload) => {
    return oocFlagIChartColumns.find((f) => !!payload[f]) !== undefined;
  };

  const checkEverFlagsWarning = (payload) => {
    const shouldShowEverFlags = payload.omitFromSqc === 'Y' || payload.silence === 'Y';

    if (shouldShowEverFlags && payload.everFlags?.length) {
      const flags = getEverFlagsString(payload);
      return flags.some((flag) => {
        const trimmedFlag = flag.trim();
        return trimmedFlag.startsWith('Warnings:') || trimmedFlag.startsWith('W:');
      });
    }
    return false;
  };

  const checkEverFlagsOOC = (payload) => {
    const shouldShowEverFlags = payload.omitFromSqc === 'Y' || payload.silence === 'Y';
    if (shouldShowEverFlags && payload.everFlags?.length) {
      const flags = getEverFlagsString(payload);
      return flags.some((flag) => {
        const trimmedFlag = flag.trim();
        return trimmedFlag.startsWith('OOC:');
      });
    }

    return false;
  };

  const CustomizedDot = ({ cx, cy, payload, dataKey }) => {
    let dotToReturn = [];
    if (dataKey === 'resultOmits') {
      const isWarning = checkWarning(payload);
      const isOOC = checkOOC(payload);

      const isEverFlagsWarning = checkEverFlagsWarning(payload);
      const isEverFlagsOOC = checkEverFlagsOOC(payload);

      dotToReturn = createCustomizedDot(
        cx,
        cy,
        payload,
        setupDataChecked,
        isWarning,
        isOOC,
        warningFlagsChecked,
        outOfControlChecked,
        everFlagsChecked,
        isEverFlagsWarning,
        isEverFlagsOOC
      );
    }

    return dotToReturn;
  };

  const renderDataLines = (rcc) => {
    if (rcc) {
      return (
        <>
          {lines
            .filter((l) => !l.reference)
            .map((line) => (
              <Line
                type="linear"
                key={line.yAxis || line.name}
                isAnimationActive={false}
                dataKey={line.yAxis}
                stroke={line.color}
                name={line.name}
                dot={<CustomizedDot key={uuidv4()} />}
              />
            ))}
        </>
      );
    }
    return (
      <>
        <Line
          type="linear"
          key={'resultOmits' || 'Data'}
          isAnimationActive={false}
          dataKey="resultOmits"
          stroke="#32CD32"
          name="Data"
          dot={<CustomizedDot key={uuidv4()} />}
        />
      </>
    );
  };

  const EverFlagsTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      // Show everFlag if the omitFromSqc or silence is Y
      const shouldShowEverFlags = data.omitFromSqc === 'Y' || data.silence === 'Y';

      if (shouldShowEverFlags && data.everFlags && data.everFlags.length) {
        const flags = getEverFlagsString(data);
        return (
          <div className="custom-tooltip">
            <p>{label}</p>
            <p
              style={{
                color: 'green'
              }}
            >
              Data: {data.result}
            </p>
            <div>
              {flags.map((flag, index) => {
                let color = 'black';
                if (flag.includes('Warnings:') || flag.includes('W:')) {
                  color = 'gold';
                } else if (flag.includes('OOC:')) {
                  color = 'red';
                }
                return (
                  <p
                    key={`flag-${index}`}
                    style={{
                      color: color
                    }}
                  >
                    {flag.trim()}
                  </p>
                );
              })}
            </div>
          </div>
        );
      }

      return (
        <div className="custom-tooltip">
          <p>{label}</p>
          <p
            style={{
              color: 'green'
            }}
          >
            Data: {data.result}
          </p>
          <p>None</p>
        </div>
      );
    }

    return null;
  };

  const getEverFlagsString = (data) => {
    if (!data.everFlags || data.everFlags.length === 0) {
      return [];
    }
    let flagsString;
    // Handle both object-based and string-based everFlags
    if (typeof data.everFlags[0] === 'string') {
      // If the array contains strings, join them directly
      flagsString = data.everFlags.join(';');
    } else {
      // If the array contains objects, find the latest version
      const latestFlag = data.everFlags.reduce((latest, current) => {
        if (typeof current === 'string') return latest; // Ignore strings during version comparison
        return current.version > (latest.version || 0) ? current : latest;
      }, {});

      // Extract flags string from the latest object
      flagsString = latestFlag.flags || '';
    }

    // Split the flags string into individual parts
    return String(flagsString).split(';');
  };

  const uniqueParameters = Array.from(
    new Set(
      data
        .map((d) => {
          if (d.fullKey) {
            const parts = d.fullKey.split('|');
            return parts[parts.length - 1];
          }
          return d.parameter;
        })
        .filter(Boolean)
    )
  );

  return (
    <>
      <h5>
        I-Chart
        {uniqueParameters?.length
          ? `: ${uniqueParameters.join(', ')}${unit?.trim() ? `, ${unit}` : ''}`
          : unit?.trim()
            ? `: ${unit}`
            : ''}
      </h5>

      <div className="checkbox-buttons-container">
        <div className="checkbox-buttons">
          Setup data = 1 <Form.Check value={setupDataChecked} onChange={handleSetupDataCheck} />
        </div>
        <div className="checkbox-buttons">
          Warning flags{' '}
          <Form.Check checked={warningFlagsChecked} onChange={handleWarningFlagCheck} />
        </div>
        <div className="checkbox-buttons">
          Out of Control{' '}
          <Form.Check checked={outOfControlChecked} onChange={handleOutOfControlCheck} />
        </div>
        <div className="checkbox-buttons">
          Reference Lines <Form.Check checked={runChartsChecked} onChange={handleRunChartsCheck} />
        </div>
        {isEverFlags && (
          <div className="checkbox-buttons">
            EverFlags <Form.Check checked={everFlagsChecked} onChange={handleEverFlagsCheck} />
          </div>
        )}
      </div>
      <ResponsiveContainer height={400}>
        <LineChart
          data={data}
          syncId={syncId}
          margin={{
            top: 20,
            right: 20,
            bottom: 15,
            left: 20
          }}
        >
          <Legend verticalAlign="top" margin height={80} />
          {isSpcDashboard && (
            <Customized
              component={({ width }) => (
                <foreignObject x={0} y={40} width={width} height={100}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: '50%'
                    }}
                  >
                    <h3 className="info-spc-container">
                      <span className="info-spc-label">Process Mean: </span>
                      {isFinite(parseFloat(selectedSummaryInfo?.mean_process_or))
                        ? parseFloat(selectedSummaryInfo.mean_process_or).toFixed(2)
                        : '--'}

                      <span className="info-spc-label">Process Range Mean: </span>
                      {isFinite(parseFloat(selectedSummaryInfo?.r_bar_sd))
                        ? parseFloat(selectedSummaryInfo.r_bar_sd).toFixed(2)
                        : '--'}
                      {config?.adjustBlueStatisticsLineSpcCharts ? (
                        <>
                          <span className="info-spc-label">Process std dev, theta-hat: </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.stdev_process_or))
                            ? parseFloat(selectedSummaryInfo.stdev_process_or).toFixed(2)
                            : '--'}
                          <span className="info-spc-label">UCL x-bar: </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.ucl_sd))
                            ? parseFloat(selectedSummaryInfo.ucl_sd).toFixed(2)
                            : '--'}
                          <span className="info-spc-label">LCL x-bar: </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.lcl_sd))
                            ? parseFloat(selectedSummaryInfo.lcl_sd).toFixed(2)
                            : '--'}
                        </>
                      ) : (
                        <>
                          <span className="info-spc-label">Process Capability (Cp): </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.Cp))
                            ? parseFloat(selectedSummaryInfo.Cp).toFixed(2)
                            : '--'}

                          <span className="info-spc-label">Process Capability (Cpk): </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.Cpk))
                            ? parseFloat(selectedSummaryInfo.Cpk).toFixed(2)
                            : '--'}

                          <span className="info-spc-label">Percent Yield: </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.percent_yield))
                            ? parseFloat(selectedSummaryInfo.percent_yield).toFixed(2)
                            : '--'}
                        </>
                      )}
                    </h3>
                  </div>
                </foreignObject>
              )}
            />
          )}
          {renderDataLines(runChartsChecked)}
          {renderReferenceLines(runChartsChecked)}
          <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
          <XAxis dataKey={xAxis} tickFormatter={formatXAxis} />
          <YAxis type="number" tickFormatter={formatYAxis} domain={[yMin, yMax]} />
          <Brush dataKey={xAxis} />
          {everFlagsChecked ? (
            <Tooltip content={<EverFlagsTooltip />} />
          ) : uniqueParameters.length > 1 ? (
            <Tooltip
              formatter={(value) => `${renderSigDigits(value)}`}
              labelFormatter={(label) => {
                let parameter = '';
                if (data && data[label] && data[label].fullKey) {
                  const parts = data[label].fullKey.split('|');
                  parameter = parts[parts.length - 1];
                } else if (data && data[label] && data[label].parameter) {
                  parameter = data[label].parameter;
                }
                return `${label}${parameter ? ` - ${parameter}` : ''}`;
              }}
            />
          ) : (
            <Tooltip formatter={(value) => `${renderSigDigits(value)}`} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
}

SQCLineChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  lines: PropTypes.arrayOf(PropTypes.object),
  xAxis: PropTypes.string.isRequired,
  syncId: PropTypes.string.isRequired,
  yMin: PropTypes.number.isRequired,
  yMax: PropTypes.number.isRequired,
  controlStrategyConfigurations: null
};

export default SQCLineChart;
